/**
 * I passi del tutorial guidato dell'elenco Marche.
 *
 * Due preparativi: il menu del piu' in alto e il menu della riga.
 * `stopBrandsListTour` rimette tutto com'era.
 *
 * I bersagli della pagina sono scoped; le voci dei menu no, perche' Vuetify
 * stacca il contenuto dei menu e lo appende a `[data-app]`, fuori dalla pagina.
 */
const at = name => `[data-tour-scope="brands-list"] [data-tour="${name}"]`;

const ADD_BRAND = '[data-tour="add-brand"]';
const ROW_EDIT = '[data-tour="row-edit"]';

function isVisible(selector) {
	const element = document.querySelector(selector);

	if (!element || element.getClientRects().length === 0) {
		return false;
	}

	/*
	 * Un menu che si sta chiudendo resta a schermo per tutta l'animazione, ma
	 * Vuetify gli toglie subito `menuable__content__active`. Senza questo
	 * controllo un menu in chiusura sembrerebbe ancora aperto e il click di
	 * pulizia, invece di chiuderlo, lo riaprirebbe.
	 */
	const content = element.closest('.v-menu__content');

	return !content || content.classList.contains('menuable__content__active');
}

/**
 * Lascia scattare la chiusura che Vuetify ha gia' in coda.
 *
 * I menu si chiudono al primo click fuori, e il click su "Avanti" e' fuori: la
 * direttiva l'ha registrato in cattura, prima ancora che il pulsante facesse il
 * suo lavoro. Riaprendo subito, il menu si richiuderebbe un istante dopo.
 */
function afterPendingClose() {
	return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * Aspetta che il bersaglio compaia o sparisca.
 *
 * L'attesa sta qui e non nel `timeout` del passo: cosi' si aspetta solo quando
 * c'e' davvero qualcosa da aprire, e a elenco vuoto il passo si aggancia subito
 * in basso invece di far aspettare a vuoto.
 */
async function waitFor(probe, wanted, timeout = 1500) {
	const deadline = performance.now() + timeout;

	while (performance.now() < deadline) {
		if (isVisible(probe) === wanted) {
			return;
		}

		await new Promise(resolve => setTimeout(resolve, 50));
	}
}

function click(selector) {
	const element = document.querySelector(selector);

	if (element) {
		element.click();
	}
}

async function openMenu(activator, probe) {
	await afterPendingClose();

	if (isVisible(probe)) {
		return;
	}

	const element = document.querySelector(activator);

	if (!element) {
		return;
	}

	element.click();
	await waitFor(probe, true);
}

async function closeMenus() {
	await afterPendingClose();

	// L'attivatore fa da interruttore: il secondo click richiude.
	// Senza Magento il piu' e' un bottone diretto (niente menu, niente `add`): non c'e' niente da chiudere.
	if (document.querySelector(at('add')) && isVisible(ADD_BRAND)) {
		click(at('add'));
		await waitFor(ADD_BRAND, false);
	}

	if (isVisible(ROW_EDIT)) {
		click(at('row-menu'));
		await waitFor(ROW_EDIT, false);
	}
}

const openAddMenu = () => openMenu(at('add'), ADD_BRAND);
const openRowMenu = () => openMenu(at('row-menu'), ROW_EDIT);


/** Rimette la pagina com'era: menu chiusi. */
export async function stopBrandsListTour() {
	await closeMenus();
}

export function createBrandsListTourSteps() {
	return [
		{
			body: "Le marche sono una sola per prodotto - un articolo ha una marca, non tre - e sul sito non sono solo un'etichetta: ogni marca ha una sua pagina, con descrizione, logo e il link al sito del produttore. Da qui le si crea, si spengono e si vede quanti prodotti ne portano il nome.",
			key: 'intro',
			placement: 'bottom',
			target: at('tutorial'),
			title: 'A cosa serve questa pagina',
		},
		{
			body: "Cerca nel nome della marca, nella lingua in cui e' scritta. Funziona da quattro caratteri in su - sotto, la ricerca parte ma viene ignorata e l'elenco resta intero. Si conferma con Invio o con la lente.",
			before: closeMenus,
			key: 'search',
			placement: 'bottom',
			target: at('search'),
			title: 'Cercare una marca',
		},
		{
			body: "Le colonne dicono quando la marca e' stata creata, il nome, le lingue, l'indirizzo del sito del produttore e Reference, cioe' quanti prodotti del catalogo portano quella marca. Reference e' il numero da guardare prima di toccare qualsiasi cosa: dice quanto pesa quella marca sul catalogo.",
			key: 'table',
			placement: 'top',
			target: at('table'),
			timeout: 250,
			title: 'Come si legge la tabella',
		},
		{
			body: "Una bandierina per ogni lingua del negozio: a colori dove il nome della marca e' scritto, in grigio dove manca. Se scrivi il nome in una sola lingua, al salvataggio vale anche per le altre lasciate vuote: cosi' la marca non sparisce dalle pagine del sito nelle altre lingue ne' dai dati per Google.",
			key: 'langs',
			placement: 'bottom',
			target: at('langs'),
			timeout: 250,
			title: 'Le lingue della marca',
		},
		{
			body: "L'interruttore accende e spegne la marca, e il cambio parte subito: non c'e' un salvataggio da confermare. Spegnerla non cancella niente e non tocca i prodotti - smette solo di mostrarne la pagina sul sito.",
			key: 'status',
			placement: 'right',
			target: at('status'),
			timeout: 250,
			title: 'Accendere e spegnere',
		},
		{
			body: 'I tre pallini a inizio riga aprono due voci: Modifica apre la pagina della marca - nome, descrizione, logo, dati per Google - ed Elimina la cancella, chiedendo conferma. Guarda Reference prima di eliminare: quei prodotti restano senza marca.',
			before: openRowMenu,
			key: 'row-edit',
			placement: 'right',
			target: ROW_EDIT,
			timeout: 250,
			title: 'Il menu della riga',
		},
		{
			body: "Il piu' apre la pagina della nuova marca, che ha un tutorial tutto suo. Se hai Magento collegato, il piu' apre invece un menu: oltre ad Aggiungi marca c'e' la voce per importare le marche da li' invece di riscriverle a mano.",
			before: openAddMenu,
			key: 'add-brand',
			placement: 'left',
			target: ADD_BRAND,
			title: 'Aggiungere una marca',
		},
	];
}
